import React from 'react';
import { Briefcase } from 'lucide-react';
import type { PortfolioStatsData } from '../../types/expert';

interface PortfolioStatsCardProps {
  data: PortfolioStatsData;
}

const PortfolioStatsCard: React.FC<PortfolioStatsCardProps> = ({ data }) => {
  const formatCurrency = (value: number) =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: data.currency || 'USD',
      minimumFractionDigits: 2,
    }).format(value);

  return (
    <div className="rounded-xl border border-teal-500/20 bg-slate-800/80 p-4">
      {/* Header */}
      <div className="flex items-center gap-2 mb-3">
        <Briefcase size={16} className="text-teal-400" />
        <span className="text-sm font-semibold text-slate-200">
          {data.portfolioName || 'Portfolio'}
        </span>
      </div>

      {/* Total value */}
      <div className="mb-3">
        <p className="text-xs text-slate-400">Total Value</p>
        <p className="text-xl font-bold text-white font-mono tabular-nums">
          {formatCurrency(data.totalValue)}
        </p>
      </div>

      {/* Holdings count */}
      <p className="text-xs text-slate-400 mb-3">
        {data.holdingsCount} holding{data.holdingsCount !== 1 ? 's' : ''}
      </p>

      {/* Top holdings with allocation bars */}
      {data.topHoldings && data.topHoldings.length > 0 && (
        <div className="space-y-2">
          {data.topHoldings.map((h) => (
            <div key={h.symbol}>
              <div className="flex items-center justify-between text-xs mb-0.5">
                <span className="text-slate-200 font-medium">{h.symbol}</span>
                <span className="text-slate-400 font-mono tabular-nums">
                  {h.allocation.toFixed(1)}%
                </span>
              </div>
              <div className="h-1.5 bg-slate-700 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-teal-500 to-emerald-500"
                  style={{ width: `${Math.min(h.allocation, 100)}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PortfolioStatsCard;
